import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { UiSelect, UiTab, UiTable, UiButton } from "../../../components";
import { CUSTOMER_TRANSACTION_COLUMNS, LAWYER_TRANSACTION_COLUMNS, TRANSACTION_TAB_DATA, lawyerActionDropdownMenu, customerActionDropdownMenu, TRANSACTION_STATUS_OPTIONS, HISTORY_COLUMNS, TRANSACTION_CATEGORIES } from "./config";
import "./style.scss";
import { Dropdown, Menu, Space, Modal } from "antd";
import { actionDots, actionViewIcon, downloadReport } from "../../../assets/images";
import { CUSTOMERMANAGEMENT, CONSULTATION, TRANSACTIONMANAGEMENT, PAYMENTMANAGEMENT, LAWYERMANAGEMENT } from "../../../apiServices/endpoints";
import * as api from "../../../actions";
import { searchInitiateDelayTime, tablePageSizeOptions } from "../../../utils/appUtils";
import { loaderReducer } from "../../../reducer/loader.reducer";
import TransactionHistory from "./history";
import dayjs from "dayjs";
import ConsultationDrawer from "./consultaionDrawer";
import { showErrorNotification, showSuccessNotification } from "../../../components/uiNotification";
import { useReactToPrint } from "react-to-print";
import UnpaidLawyerTransactionReport from "./report";

const PrintUnpaidLawyerTransactionReport = React.forwardRef((props, ref) => {
    return (
        <div ref={ref}>
            <UnpaidLawyerTransactionReport
                data={props?.data}
                searchFilter={props?.searchFilter}
                totalElements={props?.totalElements}
                totalBalanceAmount={props?.totalBalanceAmount}
            />
        </div>
    );
});

const INITIAL_FILTER = {
    activeTab: "LAWYERS", activeTabKey: "1", searchFilter: "", searchTermValue: "", status: "UNPAID", page: 0, size: 10
};
const INITIAL_HISTORY_FILTER = {
    month: null, startDate: null, endDate: null, category: null, status: null, page: 0, size: 10
};
const INITIAL_CONSULTATION_DRAWER = {
    open: false, loading: false, lawyer: null, consultations: []
};

const Transactions = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const reportRef = useRef(null);
    const debounceTimeOut = useRef(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [lawyerData, setLawyerData] = useState([]);
    const [lawyerReportData, setLawyerReportData] = useState([]);
    const [customerData, setCustomerData] = useState([]);
    const [historyResultData, setHistoryResultData] = useState(null);
    const [lawyerTotalElements, setLawyerTotalElements] = useState(0);
    const [lawyerReportSummary, setLawyerReportSummary] = useState({ totalElements: 0, totalBalanceAmount: 0 });
    const [customerTotalElements, setCustomerTotalElements] = useState(0);
    const [filter, setFilter] = useState(INITIAL_FILTER);
    const [historyFilter, setHistoryFilter] = useState(INITIAL_HISTORY_FILTER);
    const [consultationDrawer, setConsultationDrawer] = useState(INITIAL_CONSULTATION_DRAWER);
    const [paymentModal, setPaymentModal] = useState({ open: false, record: null, loading: false });
    const [refundModal, setRefundModal] = useState({ open: false, record: null, amount: "", loading: false });
    const [isReportRequested, setIsReportRequested] = useState(false);
    const configResults = useSelector((state) => state?.configAction?.configData);
    const configGstPercentage = configResults?.find((detail) => detail?.configId === "lawyerFeePercentage")?.configuration?.[0] || 0;
    const [searchOptions, setSearchOptions] = useState([]);
    const [searchValue, setSearchValue] = useState("");

    const getLawyerTransactions = (page, size, searchTerm, status) => api.get(`${CONSULTATION}/lawyer_unpaid-consultations?page=${page}&size=${size}&paidStatus=${status}${searchTerm ? `&search=${searchTerm}` : ""}`);
    const getCustomerTransactions = (page, size, searchTerm) => api.get(`${CUSTOMERMANAGEMENT}/transaction_customer_details?page=${page}&size=${size}${searchTerm ? `&search=${searchTerm}` : ""}`);
    const getLawyerHistory = (id, page, size, category, startDate, endDate, status, month) => api.get(`${TRANSACTIONMANAGEMENT}/lawyer_transaction/${id}?page=${page}&size=${size}${category ? `&paymentCategory=${category}` : ""}${startDate ? `&startDate=${dayjs(startDate).format("YYYY-MM-DD")}` : ""}${endDate ? `&endDate=${dayjs(endDate).format("YYYY-MM-DD")}` : ""}${status ? `&paymentStatus=${status}` : ""}${month ? `&month=${dayjs(month).format("YYYY-MM")}` : ""}`);
    const getCustomerHistory = (id, page, size, category, startDate, endDate, status, month) => api.get(`${TRANSACTIONMANAGEMENT}/customer_transaction/${id}?page=${page}&size=${size}${category ? `&paymentCategory=${category}` : ""}${startDate ? `&startDate=${dayjs(startDate).format("YYYY-MM-DD")}` : ""}${endDate ? `&endDate=${dayjs(endDate).format("YYYY-MM-DD")}` : ""}${status ? `&paymentStatus=${status}` : ""}${month ? `&month=${dayjs(month).format("YYYY-MM")}` : ""}`);
    const getMultipleConsultations = (consultationIds) => api.post(`${CONSULTATION}/get_multiple_consultation`, consultationIds);
    const getTransactionById = (transactionId) => api.get(`${TRANSACTIONMANAGEMENT}/${transactionId}`);
    const sendAmount = (payload) => api.post(`${PAYMENTMANAGEMENT}/send_Amount`, payload);
    const refundCustomerAmount = (payload) => api.put(`${CUSTOMERMANAGEMENT}/refund_customer`, payload);
    const getLawyerName = (search) => api.get(`${LAWYERMANAGEMENT}/get_lawyer_name?lawyerSearch=${search}`);
    const getCustomerName = (search) => api.get(`${CUSTOMERMANAGEMENT}/get_customer_name?customerSearch=${search}`);

    const { isFetching: isPaying } = useQuery(["PAY_AMOUNT"], sendAmount, {
        onSuccess: (response) => {
            const data = response?.data ?? response;
            if (data?.statusCode === 200 || data?.statusCode === 201 || data?.status === "OK") {
                showSuccessNotification("Success", "Payment processed successfully");
                setPaymentModal({ open: false, record: null, loading: false });
                refetchLawyerTransactions();
            } else {
                showErrorNotification("Error", data?.error?.message || response?.response?.data?.error?.message || "Payment processing failed");
                setPaymentModal((previousState) => ({ ...previousState, loading: false }));
            }
        },
        onError: (error) => {
            showErrorNotification("Error", error?.message || "Payment processing failed");
            setPaymentModal((previousState) => ({ ...previousState, loading: false }));
        },
        enabled: false,
        refetchOnWindowFocus: false
    });

    const { isFetching: isFetchingLawyerNames } = useQuery(
        ["FETCH_LAWYER_NAMES", searchValue],
        () => getLawyerName(searchValue),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const options = response?.result?.lawyerNameDropdownResponses?.map(item => ({
                        label: item?.fullName,
                        value: item?.userId,
                        key: item?.userId
                    })) || [];
                    setSearchOptions(options);
                } else {
                    setSearchOptions([]);
                }
            },
            enabled: filter?.activeTabKey === "1" && !!searchValue,
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingCustomerNames } = useQuery(
        ["FETCH_CUSTOMER_NAMES", searchValue],
        () => getCustomerName(searchValue),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const options = response?.result?.customerNameDropdowns?.map(item => ({
                        label: item?.fullName,
                        value: item?.userId,
                        key: item?.userId
                    })) || [];
                    setSearchOptions(options);
                } else {
                    setSearchOptions([]);
                }
            },
            enabled: filter?.activeTabKey === "2" && !!searchValue,
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingLawyer, refetch: refetchLawyerTransactions } = useQuery(
        ["FETCH_LAWYER_TRANSACTIONS", filter?.page, filter?.size, filter?.searchTermValue, filter?.status],
        () => getLawyerTransactions(filter?.page, filter?.size, filter?.searchTermValue, filter?.status === "PAID" ? "PAID" : "UNPAID"),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const content = response?.result?.unpaidLawyerConsultationResponses?.content || [];
                    setLawyerData(content);
                    setLawyerTotalElements(response?.result?.unpaidLawyerConsultationResponses?.totalElements || 0);
                } else {
                    setLawyerData([]);
                    setLawyerTotalElements(0);
                }
            },
            enabled: filter?.activeTabKey === "1" && Boolean(filter?.page || filter?.size || filter?.searchTermValue || filter?.status),
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingLawyerReport } = useQuery(
        ["FETCH_LAWYER_TRANSACTIONS_REPORT", filter?.searchTermValue, filter?.status, filter?.isReportOpen],
        () => getLawyerTransactions(0, -1, filter?.searchTermValue, "UNPAID"),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const content = response?.result?.unpaidLawyerConsultationResponses?.content || [];
                    setLawyerReportData(content);
                    setLawyerReportSummary({
                        totalElements: response?.result?.unpaidLawyerConsultationResponses?.totalElements || content?.length || 0,
                        totalBalanceAmount: content?.reduce((sum, record) => sum + (Number(record?.totalAmount) || 0), 0) || 0
                    });
                } else {
                    setLawyerReportData([]);
                    setLawyerReportSummary({ totalElements: 0, totalBalanceAmount: 0 });
                }
            },
            enabled: filter?.activeTabKey === "1" && filter?.status === "UNPAID" && filter?.isReportOpen,
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingCustomer } = useQuery(
        ["FETCH_CUSTOMER_TRANSACTIONS", filter?.page, filter?.size, filter?.searchTermValue],
        () => getCustomerTransactions(filter?.page, filter?.size, filter?.searchTermValue),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const content = response?.result?.customerTransactionDetails?.content || [];
                    setCustomerData(content);
                    setCustomerTotalElements(response?.result?.customerTransactionDetails?.totalElements || 0);
                } else {
                    setCustomerData([]);
                    setCustomerTotalElements(0);
                }
            },
            enabled: filter?.activeTabKey === "2" && Boolean(filter?.page || filter?.size || filter?.searchTermValue),
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingHistory } = useQuery(
        ["FETCH_TRANSACTION_HISTORY", filter?.activeTabKey, selectedUser, historyFilter],
        () => {
            const { page, size, category, startDate, endDate, status, month } = historyFilter;
            if (filter?.activeTabKey === "1") {
                const id = selectedUser?.lawyerId;
                return getLawyerHistory(id, page, size, category, startDate, endDate, status, month);
            } else {
                const id = selectedUser?.customerId;
                return getCustomerHistory(id, page, size, category, startDate, endDate, status, month);
            }
        },
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            enabled: !!selectedUser,
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const data = filter?.activeTabKey === "1" ? response?.result?.lawyerTransactions : response?.result?.customerTransactions;
                    setHistoryResultData(data);
                } else {
                    showErrorNotification("Error", response?.message || "Failed to fetch transaction history");
                    setHistoryResultData(null);
                }
            },
        }
    );

    const handleTransactionReportPrint = useReactToPrint({
        content: () => reportRef.current,
        onAfterPrint: () => {
            setFilter((previousFilter) => ({ ...previousFilter, isReportOpen: false }));
            setIsReportRequested(false);
            setLawyerReportData([]);
            setLawyerReportSummary({ totalElements: 0, totalBalanceAmount: 0 });
        },
        pageStyle: `@page {
                size: A4 landscape !important;
                margin: 8mm !important;
            }
            body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            .transaction-report::before {
                content: "";
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                border: 2px solid #005D60;
                z-index: 9999;
                pointer-events: none;
            }`
    });

    const handleHistoryPagination = (page, size) => {
        setHistoryFilter(prev => ({ ...prev, page: page - 1, size }));
    };

    const handleHistoryBack = () => {
        setSelectedUser(null);
        setHistoryFilter(INITIAL_HISTORY_FILTER);
        setHistoryResultData(null);
    };

    const handleTabChange = (key) => {
        const tab = TRANSACTION_TAB_DATA?.find((tab) => tab?.key === key);
        if (tab) {
            setFilter({ ...INITIAL_FILTER, activeTab: tab?.content, activeTabKey: key });
            setSearchOptions([]);
            setSearchValue("");
        }
    };

    const handleDropdownSearch = (value) => {
        if (debounceTimeOut?.current) {
            clearTimeout(debounceTimeOut?.current);
        }
        if (value) {
            debounceTimeOut.current = setTimeout(() => {
                setSearchValue(value);
            }, searchInitiateDelayTime);
        } else {
            setSearchValue("");
            setSearchOptions([]);
        }
    };

    const handleSelectSearch = (value) => {
        setFilter(prev => ({ ...prev, searchFilter: value, searchTermValue: value, page: 0, size: 10 }));
    };

    const handleSelectChange = (value) => {
        setFilter(prev => ({ ...prev, status: value, page: 0, size: 10, isReportOpen: false }));
    };

    const handlePagination = (page, size) => {
        setFilter(prev => ({ ...prev, page: page - 1, size: size }));
    };

    const handleDownloadReport = () => {
        setFilter((previousFilter) => ({ ...previousFilter, isReportOpen: true }));
        setIsReportRequested(true);
    };

    const handleMenuClick = async (menuType, detail) => {
        if (menuType === "consultationView") {
            const consultationIds = detail?.consultationIds || [];
            if (!consultationIds?.length) return;
            setConsultationDrawer({ ...INITIAL_CONSULTATION_DRAWER, loading: true, lawyer: detail });
            try {
                const response = await getMultipleConsultations(consultationIds);
                const data = response?.data ?? response;
                if (data?.statusCode === 200 || data?.status === "OK") {
                    const consultations = data?.result?.consultationResponses || [];
                    const apiLawyer = consultations?.[0]?.lawyerResponse;
                    setConsultationDrawer({
                        open: true,
                        loading: false,
                        consultations,
                        lawyer: apiLawyer ? { ...detail, ...apiLawyer, lawyerName: `${apiLawyer?.salutation || ""} ${apiLawyer?.firstName || ""} ${apiLawyer?.middleName || ""} ${apiLawyer?.lastName || ""}`.replace(/\s+/g, " ").trim() } : detail
                    });
                } else {
                    throw new Error(data?.error?.message || "Failed to fetch consultations");
                }
            } catch (error) {
                showErrorNotification("Error", error?.message || "Failed to load consultation details");
                setConsultationDrawer(INITIAL_CONSULTATION_DRAWER);
            }
        } else if (menuType === "history") {
            setSelectedUser(detail);
        } else if (menuType === "refund") {
            setRefundModal({ open: true, record: detail, amount: "", loading: false });
        }
    };

    const handleConsultationDrawerClose = () => {
        setConsultationDrawer(INITIAL_CONSULTATION_DRAWER);
    };

    const handlePayment = (record) => {
        setPaymentModal({ open: true, record, loading: false });
    };

    const handleConfirmPayment = async () => {
        const { record } = paymentModal;
        if (!record) return;

        setPaymentModal((previousState) => ({ ...previousState, loading: true }));

        try {
            const extendTransactionIds = (record?.consultationResponses || []).flatMap((consultation) => {
                const extendList = Array.isArray(consultation?.extendConsultionDetailsList)
                    ? consultation.extendConsultionDetailsList
                    : consultation?.extendConsultionDetailsList
                        ? [consultation.extendConsultionDetailsList]
                        : [];
                return extendList.map((extendItem) => extendItem?.transactionId);
            });

            const transactionIds = [
                ...(record?.transactionIds || []),
                ...extendTransactionIds
            ].filter(Boolean);
            const uniqueTransactionIds = [...new Set(transactionIds)];

            if (!uniqueTransactionIds.length) {
                showErrorNotification("Error", "Transaction ID not found");
                setPaymentModal((previousState) => ({ ...previousState, loading: false }));
                return;
            }

            const transactionResponses = await Promise.all(
                uniqueTransactionIds.map(async (transactionId) => {
                    const response = await getTransactionById(transactionId);
                    const responseData = response?.data ?? response;
                    if (responseData?.statusCode === 200 || responseData?.status === "OK") {
                        return responseData?.result?.transactionResponse;
                    }
                    return null;
                })
            );

            const validTransactions = transactionResponses.filter(Boolean);

            if (!validTransactions.length) {
                showErrorNotification("Error", "Failed to load transaction details");
                setPaymentModal((previousState) => ({ ...previousState, loading: false }));
                return;
            }

            queryClient.prefetchQuery(["PAY_AMOUNT"], () => sendAmount(validTransactions));
        } catch (error) {
            showErrorNotification("Error", error?.message || "Failed to process payment");
            setPaymentModal((previousState) => ({ ...previousState, loading: false }));
        }
    };

    const handleConfirmRefund = () => {
        const customerId = refundModal?.record?.customerId;
        const amount = Number(refundModal?.amount);
        if (!customerId) {
            showErrorNotification("Error", "Customer ID not found");
            return;
        }
        if (!amount || amount <= 0) {
            showErrorNotification("Error", "Please enter a valid refund amount");
            return;
        }
        setRefundModal((previousState) => ({ ...previousState, loading: true }));
        const payload = {
            customerId,
            amount
        };
        queryClient
            .prefetchQuery(["REFUND_CUSTOMER", customerId, amount], () => refundCustomerAmount(payload))
            .then((response) => {
                const responseData = response?.data ?? response;
                if (responseData?.statusCode === 200 || responseData?.statusCode === 201 || responseData?.status === "OK") {
                    showSuccessNotification("Success", "Refund processed successfully");
                    setRefundModal({ open: false, record: null, amount: "", loading: false });
                    queryClient.invalidateQueries(["FETCH_CUSTOMER_TRANSACTIONS"]);
                } else {
                    showErrorNotification("Error", response?.responseData?.error?.message || "Failed to process Refund");
                    setRefundModal((previousState) => ({ ...previousState, loading: false }));
                }
            })
    };

    useEffect(() => {
        const isLoading = isFetchingLawyer || isFetchingCustomer || isFetchingHistory || consultationDrawer?.loading || isPaying || isFetchingLawyerNames || isFetchingCustomerNames || refundModal?.loading || isFetchingLawyerReport || paymentModal?.loading;
        dispatch(loaderReducer(isLoading));
    }, [dispatch, isFetchingLawyer, isFetchingCustomer, isFetchingHistory, consultationDrawer?.loading, isPaying, isFetchingLawyerNames, isFetchingCustomerNames, refundModal?.loading, isFetchingLawyerReport, paymentModal?.loading]);

    useEffect(() => {
        if (filter?.activeTabKey === "1" && filter?.status === "UNPAID" && filter?.isReportOpen && isReportRequested && !isFetchingLawyerReport) {
            handleTransactionReportPrint();
        }
    }, [filter?.activeTabKey, filter?.status, filter?.isReportOpen, isReportRequested, isFetchingLawyerReport, handleTransactionReportPrint]);

    const menuList = (menuItems, list) => {
        return <Menu>
            {
                menuItems?.map((menuDetails) => {
                    return <Menu.Item key={menuDetails?.key}
                        onClick={(event) => {
                            event?.domEvent?.stopPropagation();
                            handleMenuClick(menuDetails?.key, list);
                        }}
                    >{menuDetails?.label}</Menu.Item>
                })
            }
        </Menu>
    };

    const actionRender = (list) => {
        if (filter?.activeTabKey === "2") {
            return (
                <div style={{ textAlign: "center" }} onClick={(event) => event?.stopPropagation()}>
                    <Dropdown overlay={menuList(customerActionDropdownMenu, list)} trigger={["click"]} placement="bottomRight">
                        <Space onClick={(event) => {
                            event?.preventDefault();
                            event?.stopPropagation();
                        }} style={{ justifyContent: "center", width: "100%", cursor: "pointer" }}>
                            <img src={actionDots} alt="Action" />
                        </Space>
                    </Dropdown>
                </div>
            );
        }

        if (filter?.activeTabKey === "1" && filter?.status === "PAID") {
            return (
                <Space
                    onClick={(event) => {
                        event?.preventDefault();
                        event?.stopPropagation();
                        handleMenuClick("history", list);
                    }}
                    style={{ justifyContent: "center", width: "100%", cursor: "pointer" }}
                >
                    <img src={actionViewIcon} alt="View" />
                </Space>
            );
        }
        const menuItems = lawyerActionDropdownMenu;
        return (
            <div style={{ textAlign: "center" }} onClick={(event) => event?.stopPropagation()}>
                <Dropdown overlay={menuList(menuItems, list)} trigger={["click"]} placement="bottomRight">
                    <Space onClick={(event) => {
                        event?.preventDefault();
                        event?.stopPropagation();
                    }} style={{ justifyContent: "center", width: "100%", cursor: "pointer" }}>
                        <img src={actionDots} alt="Action" />
                    </Space>
                </Dropdown>
            </div>
        );
    };

    const getPrimaryRowAction = () => {
        if (filter?.activeTabKey === "2" || (filter?.activeTabKey === "1" && filter?.status === "PAID")) {
            return "history";
        }
        return "consultationView";
    };

    const handleTransactionRowClick = (transactionDetail, event) => {
        if (event?.target?.closest("button")) {
            return;
        }
        const primaryAction = getPrimaryRowAction();
        handleMenuClick(primaryAction, transactionDetail);
    };

    const transactionList = historyResultData?.transactionResponses?.content || [];
    const historyTotalElements = historyResultData?.transactionResponses?.totalElements || 0;
    const columns = filter?.activeTabKey === "1" ? LAWYER_TRANSACTION_COLUMNS(actionRender, filter?.status, handlePayment) : CUSTOMER_TRANSACTION_COLUMNS(actionRender);
    const dataSource = filter?.activeTabKey === "1" ? lawyerData : customerData;
    const currentTransactionTotalElements = filter?.activeTabKey === "1" ? lawyerTotalElements : customerTotalElements;
    const shouldShowTransactionPagination = currentTransactionTotalElements > 10;
    const showLawyerDownloadReport = filter?.activeTabKey === "1" && filter?.status === "UNPAID";
    if (selectedUser) {
        return (
            <TransactionHistory
                user={selectedUser}
                activeTabKey={filter?.activeTabKey}
                onBack={handleHistoryBack}
                filter={historyFilter}
                setFilter={setHistoryFilter}
                resultData={historyResultData}
                transactionList={transactionList}
                totalElements={historyTotalElements}
                isFetching={isFetchingHistory}
                handlePagination={handleHistoryPagination}
                columns={HISTORY_COLUMNS(filter?.activeTabKey === "1")}
                categoryOptions={TRANSACTION_CATEGORIES}
            />
        );
    }
    return (
        <div>
            <div className="flexible">
                <div>
                    <h6 style={{ padding: "10px", margin: 0, whiteSpace: "nowrap" }}>Transaction's</h6>
                </div>
                <div className="flexible">
                    <div style={{ width: "300px" }}>
                        <UiSelect
                            showSearch
                            value={filter?.searchFilter || undefined}
                            placeholder={filter?.activeTabKey === "1" ? "Search Lawyer Name / ID" : "Search Customer Name / ID"}
                            defaultActiveFirstOption={false}
                            showArrow={false}
                            filterOption={false}
                            onInput={(event) => handleDropdownSearch(event.target.value)}
                            onChange={handleSelectSearch}
                            notFoundContent={null}
                            options={searchOptions}
                            allowClear
                            style={{ width: "100%" }}
                            onClear={() => {
                                setFilter(prev => ({ ...prev, searchFilter: "", searchTermValue: "", page: 0 }));
                                setSearchOptions([]);
                                setSearchValue("");
                            }}
                        />
                    </div>
                    {filter?.activeTabKey === "1" && (
                        <div>
                            <UiSelect
                                placeholder="Select Status"
                                value={filter?.status}
                                onChange={handleSelectChange}
                                options={TRANSACTION_STATUS_OPTIONS}
                                allowClear={false}
                                style={{ width: "100px" }}
                            />
                        </div>
                    )}
                    {showLawyerDownloadReport && (
                        <div>
                            <UiButton type="primary" onClick={handleDownloadReport}>
                                <img src={downloadReport} alt="Download Report" />
                                Download Report
                            </UiButton>
                        </div>
                    )}
                </div>
            </div>
            <UiTab
                tabs={TRANSACTION_TAB_DATA}
                activeTabKey={filter?.activeTabKey}
                onChange={handleTabChange}
                count={filter?.activeTabKey === "1" ? lawyerTotalElements : customerTotalElements}
            />
            <UiTable
                columns={columns}
                dataSource={dataSource}
                scroll={{ y: "calc(100vh - 280px)" }}
                onRow={(transactionDetail) => ({
                    onClick: (event) => handleTransactionRowClick(transactionDetail, event)
                })}
                pagination={!shouldShowTransactionPagination ? false : {
                    current: filter?.page + 1,
                    pageSize: filter?.size || 10,
                    total: currentTransactionTotalElements,
                    pageSizeOptions: tablePageSizeOptions,
                    showSizeChanger: true,
                    onChange: handlePagination
                }}
            />
            <ConsultationDrawer
                open={consultationDrawer?.open}
                onClose={handleConsultationDrawerClose}
                lawyer={consultationDrawer?.lawyer}
                consultations={consultationDrawer?.consultations}
                loading={consultationDrawer?.loading}
                configGstPercentage={configGstPercentage}
            />
            <Modal
                open={paymentModal?.open}
                onCancel={() => setPaymentModal({ open: false, record: null, loading: false })}
                footer={null}
                centered
                width={400}
                closable={false}
                className="payment-modal"
            >
                <div className="modal-content">
                    <p>Are you sure? to pay <br /> the Payment</p>
                    <div className="button-container">
                        <UiButton
                            onClick={() => setPaymentModal({ open: false, record: null, loading: false })}
                            className="cancel-btn"
                        >
                            Cancel
                        </UiButton>
                        <UiButton
                            loading={isPaying || paymentModal?.loading}
                            onClick={handleConfirmPayment}
                            className="pay-btn"
                        >
                            Pay Now
                        </UiButton>
                    </div>
                </div>
            </Modal>
            <Modal
                open={refundModal?.open}
                onCancel={() => setRefundModal({ open: false, record: null, amount: "", loading: false })}
                footer={null}
                centered
                width={400}
                closable={false}
                className="payment-modal"
            >
                <div className="modal-content">
                    <p>Enter Refund amount</p>
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Enter amount"
                        value={refundModal?.amount}
                        onChange={(event) => {
                            const enteredValue = event?.target?.value || "";
                            if (/^\d*$/.test(enteredValue)) {
                                setRefundModal((previousState) => ({ ...previousState, amount: enteredValue }));
                            }
                        }}
                        style={{ width: "100%", marginBottom: "12px", padding: "8px" }}
                    />
                    <div className="button-container">
                        <UiButton
                            onClick={() => setRefundModal({ open: false, record: null, amount: "", loading: false })}
                            className="cancel-btn"
                        >
                            Cancel
                        </UiButton>
                        <UiButton
                            onClick={handleConfirmRefund}
                            className="pay-btn"
                        >
                            Confirm
                        </UiButton>
                    </div>
                </div>
            </Modal>
            <div style={{ position: "absolute", left: "-10000px", top: 0, width: "100%", height: 0, overflow: "hidden" }} aria-hidden="true">
                <PrintUnpaidLawyerTransactionReport
                    ref={reportRef}
                    data={lawyerReportData}
                    searchFilter={filter?.searchTermValue}
                    totalElements={lawyerReportSummary?.totalElements}
                    totalBalanceAmount={lawyerReportSummary?.totalBalanceAmount}
                />
            </div>
        </div>
    );
};

export default Transactions;
